/**
 * A Compare step's editor (AMO-301): the two tables it checks, picked from
 * the steps before it that pass tables on; the column rows are matched on;
 * its rules; and whether a row only one table has is flagged.
 *
 * @module components/workflows/CompareParts
 */
import { WORKFLOW_LIMITS, type WorkflowCompareCheck, type WorkflowGraph, type WorkflowNode, type WorkflowTableRef } from "@amoreg/contracts";
import { PlusIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import { Switch } from "../ui/switch";
import { COMPARE_CHECKS, compareCheckLabel, compareTableSources } from "./CompareParts.logic";

type CompareNode = Extract<WorkflowNode, { type: "compare" }>;
type CompareRule = CompareNode["config"]["rules"][number];

/** A table from an earlier step: the step, and optionally the table's name (blank: the step's first). Shared with For each row. */
export function TablePicker({
  label,
  value,
  sources,
  onChange,
  side,
}: {
  readonly label: string;
  readonly value: WorkflowTableRef;
  readonly sources: ReadonlyArray<WorkflowNode>;
  readonly onChange: (next: WorkflowTableRef) => void;
  readonly side: "left" | "right";
}) {
  const chosen = sources.find((node) => node.id === value.step);
  return (
    <div className="space-y-1.5 rounded-md bg-muted/40 p-2" data-workflow-compare-table={side}>
      <span className="text-xs font-medium">{label}</span>
      <Select
        value={value.step}
        // Another step's tables have other names: a name picked for the last one is cleared.
        onValueChange={(step) => onChange({ step: typeof step === "string" ? step : "", table: step === value.step ? value.table : "" })}
      >
        <SelectTrigger size="sm" aria-label={`${label}: step`} data-workflow-compare-step={side}>
          <span className="truncate text-xs">{chosen ? chosen.label : value.step ? "A step no longer before this one" : "Choose a step"}</span>
        </SelectTrigger>
        <SelectContent>
          {sources.map((node) => (
            <SelectItem key={node.id} value={node.id}>
              {node.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={value.table}
        maxLength={200}
        placeholder="Table name (optional: the step's first)"
        aria-label={`${label}: table name`}
        className="text-xs"
        onChange={(event) => onChange({ ...value, table: event.currentTarget.value })}
        data-workflow-compare-table-name={side}
        data-step-field={`config.${side}`}
      />
    </div>
  );
}

/**
 * A rule's limit, kept as typed while it is edited (`1.` on the way to
 * `1.5`), and passed on once it reads as a number of at least 0.
 */
function LimitInput({ limit, label, onChange }: { readonly limit: number; readonly label: string; readonly onChange: (limit: number) => void }) {
  const [text, setText] = useState(String(limit));
  // A limit changed from elsewhere (undo, another person) shows, unless it is what the text already says.
  useEffect(() => {
    setText((current) => (Number(current) === limit ? current : String(limit)));
  }, [limit]);
  return (
    <Input
      type="text"
      inputMode="decimal"
      value={text}
      aria-label={label}
      className="w-24 text-xs"
      data-step-field="config.rules"
      onChange={(event) => {
        const next = event.currentTarget.value;
        setText(next);
        const value = Number(next);
        if (next.trim() !== "" && Number.isFinite(value) && value >= 0) onChange(value);
      }}
      onBlur={() => setText(String(limit))}
      data-workflow-compare-rule-limit=""
    />
  );
}

/** Edits a Compare step. `graph` is the chart being edited, for the steps before this one. */
export function CompareStepEditor({
  node,
  graph,
  onChange,
}: {
  readonly node: CompareNode;
  readonly graph: WorkflowGraph | null;
  readonly onChange: (next: WorkflowNode) => void;
}) {
  const sources = graph ? compareTableSources(graph, node.id) : [];
  const config = node.config;
  const set = (next: Partial<CompareNode["config"]>) => onChange({ ...node, config: { ...config, ...next } });
  const setRule = (index: number, next: Partial<CompareRule>) =>
    set({ rules: config.rules.map((rule, at) => (at === index ? { ...rule, ...next } : rule)) });
  const full = config.rules.length >= WORKFLOW_LIMITS.compareRules;

  return (
    <>
      {sources.length === 0 ? (
        <p className="text-[12px] leading-snug text-muted-foreground" data-workflow-compare-no-sources="">
          Link steps that pass on tables into this one first: an Intake step reading spreadsheets, an Ingestion step read
          as rows, or another Compare step.
        </p>
      ) : null}
      <TablePicker label="First table" side="left" value={config.left} sources={sources} onChange={(left) => set({ left })} />
      <TablePicker label="Second table" side="right" value={config.right} sources={sources} onChange={(right) => set({ right })} />
      <label className="block space-y-1.5">
        <span className="text-xs font-medium">Match rows on</span>
        <Input
          value={config.key}
          maxLength={WORKFLOW_LIMITS.fieldNameLength}
          placeholder="Column, e.g. ISIN or Account"
          onChange={(event) => set({ key: event.currentTarget.value })}
          data-workflow-compare-key=""
          data-step-field="config.key"
        />
        <Input
          value={config.rightKey}
          maxLength={WORKFLOW_LIMITS.fieldNameLength}
          placeholder="Its name in the second table, if different"
          aria-label="Key column in the second table"
          className="text-xs"
          onChange={(event) => set({ rightKey: event.currentTarget.value })}
          data-workflow-compare-right-key=""
          data-step-field="config.rightKey"
        />
      </label>
      <div className="space-y-1.5" data-workflow-compare-rules="">
        <span className="text-xs font-medium">Rules</span>
        {config.rules.length > 0 ? (
          <ul className="space-y-2">
            {config.rules.map((rule, index) => {
              const limit = COMPARE_CHECKS.find((choice) => choice.value === rule.check)?.limit ?? null;
              return (
                // oxlint-disable-next-line react/no-array-index-key -- Rules have no ids; their place is stable while editing.
                <li key={index} className="space-y-1 rounded-md bg-muted/40 p-2" data-workflow-compare-rule={index}>
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={rule.column}
                      maxLength={WORKFLOW_LIMITS.fieldNameLength}
                      placeholder="Column, e.g. Price"
                      aria-label={`Rule ${index + 1} column`}
                      onChange={(event) => setRule(index, { column: event.currentTarget.value })}
                      data-workflow-compare-rule-column=""
                      data-step-field="config.rules"
                    />
                    <Button
                      size="icon-xs"
                      variant="ghost"
                      aria-label={`Remove rule ${index + 1}`}
                      onClick={() => set({ rules: config.rules.filter((_, at) => at !== index) })}
                    >
                      <XIcon />
                    </Button>
                  </div>
                  <Input
                    value={rule.against}
                    maxLength={WORKFLOW_LIMITS.fieldNameLength}
                    placeholder="Against, in the second table (optional: same name)"
                    aria-label={`Rule ${index + 1} column in the second table`}
                    className="text-xs"
                    onChange={(event) => setRule(index, { against: event.currentTarget.value })}
                    data-workflow-compare-rule-against=""
                    data-step-field="config.rules"
                  />
                  <div className="flex items-center gap-1.5">
                    <Select
                      value={rule.check}
                      onValueChange={(value) => {
                        const check = COMPARE_CHECKS.find((choice) => choice.value === value)?.value;
                        if (check) setRule(index, { check: check satisfies WorkflowCompareCheck });
                      }}
                    >
                      <SelectTrigger size="sm" aria-label={`Rule ${index + 1} check`} data-workflow-compare-rule-check="">
                        <span className="truncate text-xs">{compareCheckLabel(rule.check)}</span>
                      </SelectTrigger>
                      <SelectContent>
                        {COMPARE_CHECKS.map((choice) => (
                          <SelectItem key={choice.value} value={choice.value}>
                            {choice.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {limit ? (
                      <LimitInput
                        limit={rule.limit}
                        label={`Rule ${index + 1} limit (${limit})`}
                        onChange={(value) => setRule(index, { limit: value })}
                      />
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-[12px] text-muted-foreground">No rules yet.</p>
        )}
        <Button
          size="sm"
          variant="outline"
          disabled={full}
          onClick={() => set({ rules: [...config.rules, { column: "", against: "", check: "percent", limit: 10 }] })}
          data-workflow-compare-add-rule=""
        >
          <PlusIcon />
          Add rule
        </Button>
      </div>
      <label className="flex items-center gap-2 text-[12px]">
        <Switch checked={config.missing} onCheckedChange={(missing) => set({ missing })} data-workflow-compare-missing="" />
        Flag rows only one table has
      </label>
      <p className="text-[11px] leading-snug text-muted-foreground">
        No AI and nothing billed: the same tables always give the same exceptions. A key that repeats, and a value a rule
        cannot read, are always flagged. Yes when anything is flagged, with an Exceptions table later steps can save or
        send; No when nothing is.
      </p>
    </>
  );
}
